/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import ProductBadge from "@/components/atoms/product-badge";
import { Toast } from "@/components/atoms/toast";
import { sanitizeImageUrl } from "@/components/molecules/image-gallery";
import { Locales } from "@/lib/i18n";
import { __, _x } from "@/lib/i18n/functions";
import { extractCurrencySymbol, formatPrice, isSaleProduct, removeCurrencySymbol } from "@/lib/utils";
import { Ban } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import Image from "next/image";
import { StoreProduct } from "@medusajs/types";
import { ProductDocument } from "@/prismicio-types";
import { PriceToastDropdown } from "@/components/atoms/price-toast-dropdown";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { normalizeConditioning, resolveProductConditioning, toLotPrice, toPiecePrice } from "@/lib/product-units";

type ProductSummaryCardProps = {
  variant?: "default" | "secondary";
  locale: Locales;
  product: StoreProduct | undefined;
  prismicProduct: ProductDocument | undefined;
  quantity: number;
  unitPrice: number;
  reference: string | null;
};

const OrderProductCard = ({
  variant = "default",
  locale,
  product,
  prismicProduct,
  quantity,
  unitPrice,
  reference,
}: ProductSummaryCardProps) => {
  const isMobile = useIsMobile();
  const imageUrl =
    sanitizeImageUrl(product?.images?.[0]?.url || "") ||
    prismicProduct?.data?.images?.[0]?.image?.url ||
    prismicProduct?.data?.featuredImage?.url ||
    "";
  const totalPrice = unitPrice * quantity;

  const productConditioning = resolveProductConditioning(
    product?.metadata?.conditioning,
    product?.metadata?.conditioning_bac
  );
  const physicalPieceCount = normalizeConditioning(productConditioning);
  const pricePerPiece = toPiecePrice(unitPrice, productConditioning);
  const isFullImage = prismicProduct?.data.image_full_size_card || false;
  const value = formatPrice(toLotPrice(unitPrice, productConditioning));
  const isRange = product?.metadata?.is_surface_based as boolean;
  const isPriceDynamic = product?.metadata?.is_price_dynamic as boolean;

  const hasMultipleVariants = (product?.variants?.length ?? 0) > 1;

  return (
    <li className="flex items-center gap-5.5">
      <figure className="bg-product-card relative flex size-19.5 shrink-0 items-center justify-center overflow-hidden rounded-md">
        {isFullImage ? (
          <Image
            src={imageUrl || ""}
            alt={prismicProduct?.data.title ?? __("Product", locale)}
            fill
            className="absolute object-cover mix-blend-multiply"
          />
        ) : (
          <div className="relative size-[52px]">
            <Image
              src={imageUrl || ""}
              alt={prismicProduct?.data.title ?? __("Product", locale)}
              fill
              className="absolute object-contain mix-blend-multiply"
            />
          </div>
        )}
        {product?.status === "draft" && (
          <Tooltip>
            <TooltipTrigger className="absolute -top-1 -left-5 -rotate-45 bg-[#FFF6E5] px-6 py-2 shadow-sm sm:hidden">
              <Ban className="h-4 w-4 -rotate-45 text-red-500" />
            </TooltipTrigger>
            <TooltipContent>Out of Stock</TooltipContent>
          </Tooltip>
        )}
      </figure>

      <dl className="flex w-full items-center justify-between gap-4">
        <div className="space-y-2">
          <dt className="sr-only">{__("Product Name", locale)}</dt>
          <dd>
            <h2 className="font-cabinet-grotesk leading-h1 text-lg font-medium sm:text-xl sm:font-bold md:text-2xl">
              {prismicProduct?.data.title}
            </h2>
            {reference && (
              <p className="text-primary-light text-sm">
                {__("REF", locale)} : {reference}
              </p>
            )}
            {isSaleProduct(product) && <Badge variant="festi">{__("To sell", locale)}</Badge>}
          </dd>

          <div className="leading-body-md text-primary-light flex flex-wrap items-center gap-1 text-sm">
            {variant === "default" && (
              <>
                <dt>{__("Quantity", locale)} :</dt>
                <dd>{quantity} | </dd>
              </>
            )}
            {product?.status === "draft" && (
              <Toast
                id={`order-${product?.id}`}
                type="info"
                className="min-[384px]:min-w-none mb-0.5 w-fit max-w-none py-2 shadow-none max-sm:hidden max-sm:p-2"
                titleClassName="max-sm:text-[10px]"
                title={__("This product is out of stock", locale)}
              />
            )}
            {physicalPieceCount > 1 && (
              <>
                <dt className="sr-only">{__("Price per piece", locale)}</dt>
                <dd>
                  {formatPrice(pricePerPiece, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  /{__("piece", locale)}
                </dd>
              </>
            )}

            {quantity > 1 && !isRange && !isPriceDynamic && (
              <ProductBadge text={_x("Price : {{ value }}", { value }, locale)} />
            )}
            {quantity > 1 && isRange && hasMultipleVariants && (
              <ProductBadge text={_x("Price : {{ value }}", { value }, locale)} />
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1">
          {isPriceDynamic || (isRange && !hasMultipleVariants) ? (
            <PriceToastDropdown locale={locale} showOnlyIcon={isMobile} />
          ) : (
            <>
              <dd className="sr-only">{__("Price", locale)}</dd>
              <dt className="text-lg font-medium sm:text-xl">
                {removeCurrencySymbol(formatPrice(totalPrice))}
                <sup>{extractCurrencySymbol(formatPrice(totalPrice))}</sup>
              </dt>
            </>
          )}

          {variant === "secondary" && (
            <>
              <dd className="sr-only">{__("Quantity", locale)}</dd>
              <dt className="text-primary-light min-w-[80px] text-right text-base sm:text-lg">
                {quantity} {quantity === 1 ? __("piece", locale) : __("pieces", locale)}
              </dt>
            </>
          )}
        </div>
      </dl>
    </li>
  );
};

export default OrderProductCard;
